// Chat History Service
// Stores and retrieves unlimited conversation history from Firestore
// AI uses last 5 messages for efficient context

import { db } from '../config/firebase';
import { isFirestoreAvailable } from './firestoreService';
import {
    collection,
    doc,
    setDoc,
    getDocs,
    deleteDoc,
    query,
    orderBy,
} from 'firebase/firestore';

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
}

export interface Conversation {
    id: string;
    title: string;
    messages: ChatMessage[];
    createdAt: string;
    updatedAt: string;
}

// Generate a title from the first user message
function generateTitle(messages: ChatMessage[]): string {
    const firstUserMessage = messages.find(m => m.role === 'user');
    if (firstUserMessage) {
        // Take first 50 chars of first message
        const title = firstUserMessage.content.slice(0, 50);
        return title.length < firstUserMessage.content.length ? `${title}...` : title;
    }
    return 'New Conversation';
}

// Get all conversations for a user
export async function loadConversations(userId: string): Promise<Conversation[]> {
    if (!isFirestoreAvailable() || !userId) {
        console.log('[ChatHistory] Firestore not available or no userId');
        return [];
    }

    try {
        const conversationsRef = collection(db!, 'users', userId, 'conversations');
        const q = query(conversationsRef, orderBy('updatedAt', 'desc'));
        const snapshot = await getDocs(q);

        const conversations: Conversation[] = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            conversations.push({
                id: doc.id,
                title: data.title || 'Untitled',
                messages: data.messages || [],
                createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || new Date().toISOString(),
            });
        });

        console.log(`[ChatHistory] Loaded ${conversations.length} conversations`);
        return conversations;
    } catch (error) {
        console.error('[ChatHistory] Error loading conversations:', error);
        return [];
    }
}

// Save or update a conversation
export async function saveConversation(userId: string, conversation: Conversation): Promise<void> {
    if (!isFirestoreAvailable() || !userId) {
        console.log('[ChatHistory] Firestore not available or no userId');
        return;
    }

    try {
        const conversationRef = doc(db!, 'users', userId, 'conversations', conversation.id);

        // Auto-generate title if not set
        const title = conversation.title === 'New Conversation'
            ? generateTitle(conversation.messages)
            : conversation.title;

        await setDoc(conversationRef, {
            title,
            messages: conversation.messages,
            createdAt: conversation.createdAt,
            updatedAt: new Date().toISOString(),
        });

        console.log(`[ChatHistory] Saved conversation ${conversation.id}`);
    } catch (error) {
        console.error('[ChatHistory] Error saving conversation:', error);
    }
}

// Delete a conversation
export async function deleteConversation(userId: string, conversationId: string): Promise<void> {
    if (!isFirestoreAvailable() || !userId) {
        return;
    }

    try {
        const conversationRef = doc(db!, 'users', userId, 'conversations', conversationId);
        await deleteDoc(conversationRef);
        console.log(`[ChatHistory] Deleted conversation ${conversationId}`);
    } catch (error) {
        console.error('[ChatHistory] Error deleting conversation:', error);
    }
}

// Get recent messages from a conversation for AI context (last N messages)
export function getRecentContext(conversation: Conversation, count: number = 5): ChatMessage[] {
    return conversation.messages.slice(-count);
}

// Format messages for AI prompt context
export function formatContextForAI(messages: ChatMessage[]): string {
    if (messages.length === 0) return '';

    return messages.map(m =>
        `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`
    ).join('\n\n');
}

// Create a new empty conversation
export function createNewConversation(): Conversation {
    return {
        id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: 'New Conversation',
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}
